import * as React from 'react'
import {Lang, LangType} from 'yougile-lang'
import {HTMLUtil, logger, Util} from 'yougile-util'
import {noPropagate} from '@containers/hooks'
import {useBoolean, useScrollState} from '../../../containers/hooks'
import {Menu} from '../../menu'
import {SVGPreload} from '../svg-preload/svg-preload'

interface Props {
  name: keyof LangType['help']
  className?: string
}

const HelpBody = ({
  html,
  className,
  maxHeight
}: {
  html: string
  className: string
  maxHeight: number
}) => {
  const bem = Util.bemFrom('help', [className])
  const [ref, isAtTop] = useScrollState()
  return (
    <div
      className={bem('body', {scrolled: !isAtTop})}
      dangerouslySetInnerHTML={{__html: html}}
      ref={ref}
      style={{maxHeight}}
    />
  )
}

const maxHeightAbsolute = 360

// todo, use base/Tooltip
export const Help = (props: Props) => {
  const {className, name} = props
  const bem = Util.bemFrom('help', [className])

  const openMenu = useBoolean(false)

  const title = Lang.msg.help[name].title
  const html = HTMLUtil.sanitizeHtml(Lang.msg.help[name].html, {
    allowedTags: ['b', 'ul', 'li', 'br', 'a', 'img'],
    allowedAttributes: {
      img: ['src', 'alt', 'style', 'data-hover'],
      a: ['href', 'target', 'rel']
    }
  })
  const large = html.length > 100
  const [maxHeight, setMaxHeight] = React.useState(maxHeightAbsolute)

  if (!title) {
    logger.log('No title', name)
    return null
  }

  return (
    <div className={bem()} onClick={openMenu.turnOn}>
      {openMenu.value && (
        <Menu
          className={bem('menu')}
          storeMaxHeight={setMaxHeight}
          onEscape={openMenu.turnOff}
        >
          <div className={bem('overlay')} onClick={openMenu.turnOff} />
          <div className={bem('wnd', {large})}>
            <div className={bem('fg')}>
              <div className={bem('header')}>
                <div className={bem('title')}>{title}</div>
                <div className={bem('close')} onClick={openMenu.turnOff} />
              </div>
              <HelpBody
                html={html}
                className={className}
                maxHeight={Math.min(maxHeightAbsolute, maxHeight - 50)}
              />
            </div>

            <div className={bem('bg')}>
              <div className={bem('corner-top-left')} />
              <div className={bem('corner-top-right')} />
              <div className={bem('corner-bottom-right')} />
              <div className={bem('corner-bottom-left')} />

              <div className={bem('side-top')} />
              <div className={bem('side-right')} />
              <div className={bem('side-bottom')} />
              <div className={bem('side-left')} />
            </div>
          </div>
        </Menu>
      )}
    </div>
  )
}

export const HelpPreload = () => (
  <>
    <SVGPreload img={['help/help']} />
    <SVGPreload
      img={[
        'help/sides/bottom',
        'help/sides/left',
        'help/sides/right',
        'help/sides/top',
        'help/corners/bottom-left-icon',
        'help/corners/bottom-left',
        'help/corners/bottom-right-icon',
        'help/corners/bottom-right',
        'help/corners/top-left-icon',
        'help/corners/top-left',
        'help/corners/top-right-icon',
        'help/corners/top-right'
      ]}
      suffixes={[]}
    />
  </>
)

export const SmallHelp = ({name}: Props) => {
  // todo: remove custom scale, translate, and sizing when a new `Help` component is implemented
  return (
    <div className="max-w-16 max-h-16">
      <div
        onClick={noPropagate}
        className="flex-none ml-3 transform scale-[0.6667] h-16 w-16 -translate-y-[2.5px] -translate-x-[5px]"
      >
        <Help name={name} />
      </div>
    </div>
  )
}
